import { Injectable } from '@angular/core';
import {CacheService} from './cache.service';
import {Observable, ReplaySubject, of} from 'rxjs';
import {map, mapTo, mergeMap, tap} from 'rxjs/operators';
import {CustomProperty, CustomPropertyWriteModel} from '../models/CustomProperty';
import {environment} from '../../environments/environment';
import {HttpResponse} from '@angular/common/http';
import {HttpService} from './http.service';

@Injectable()
export class CustomPropertyService {

    private _customerCustomProperties$: ReplaySubject<Array<CustomProperty>> = new ReplaySubject(1);
    customerCustomProperties: Array<CustomProperty>;

    get customerCustomProperties$(): Observable<Array<CustomProperty>> {
        return this._customerCustomProperties$.asObservable();
    }

    constructor(private httpService: HttpService,
                private cacheService: CacheService
    ) {

        this.customerCustomProperties$.subscribe(
            customProperties => {
                this.customerCustomProperties = customProperties;
            }
        )
    }

    getCustomerCustomProperties(skipCache: boolean = false): Observable<Array<CustomProperty>> {
        if (!skipCache && this.cacheService.hasItem('currentCustomerCustomProperties', 0)) {
            return of(this.cacheService.getItem('currentCustomerCustomProperties', 0));
        } else {
            return this.fetchCustomerCustomProperties();
        }
    }

    private fetchCustomerCustomProperties(): Observable<Array<CustomProperty>> {
        return this.httpService.get<Array<CustomProperty>>(environment.apiCommonV3BaseUrl + '/properties', {}).pipe(
            map((customerCustomProperties: Array<CustomProperty>) =>
                customerCustomProperties.filter(
                    (property) => {
                        return !(property.name.startsWith('$$') && property.name.endsWith('$$'))
                    }
                )
            ),
            tap((customerCustomProperties: Array<CustomProperty>) => this.cacheService.setItem('currentCustomerCustomProperties', 0, customerCustomProperties)),
            tap((customerCustomProperties: Array<CustomProperty>) => this._customerCustomProperties$.next(customerCustomProperties))
        );
    }

    refreshAllCustomProperties(): Observable<Array<CustomProperty>> {
        return this.fetchCustomerCustomProperties().pipe(
            tap((customProperties: Array<CustomProperty>) => {
                this._customerCustomProperties$.next(customProperties);
            })
        );
    }

    createCustomProperty(customProperty: CustomPropertyWriteModel): Observable<boolean> {
        return this.httpService.post(environment.apiCommonV3BaseUrl + '/properties', customProperty, { observe: 'response' }).pipe(
            map((res: HttpResponse<any>) => res.ok),
            mergeMap(
                () => this.refreshAllCustomProperties()
            ),
            mapTo(true)
        );
    }

    saveCustomProperty(customProperty: CustomProperty) {
        return this.httpService.patch(environment.apiCommonV3BaseUrl + '/properties/' + customProperty.id, customProperty,{ observe: 'response' }).pipe(
            tap((res: HttpResponse<any>) => {
                if (!res.ok) throw new Error('Error when saving custom property.');
            }),
            mergeMap(
                () => this.refreshAllCustomProperties()
            ),
            mapTo(true)
        );
    }

    updatePropertyStatus(propertyId: number, status: 'ENABLED' | 'DISABLED'): Observable<boolean> {
        return this.httpService.patch(environment.apiCommonV3BaseUrl + '/properties/' + propertyId, { status },{ observe: 'response' }).pipe(
            tap((res: HttpResponse<any>) => {
                if (!res.ok) throw new Error('Error when disabling custom property.');
            }),
            mergeMap(
                () => this.refreshAllCustomProperties()
            ),
            mapTo(true)
        );
    }
}
